/**
 * BaseTemplate Admin — Configuración del sitio en vivo
 *
 * Todos los cambios se aplican directamente a producción (variant: "live").
 * No existe borrador ni flujo de publicación.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  Loader2,
  Save,
  ImageIcon,
  Eye,
  RefreshCw,
  Globe,
  ArrowUpRight,
} from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";

interface SiteSettings {
  headerUrl: string;
  title: string;
  description: string;
  logoUrl: string;
  galleryImageUrls: string[];
  bannerImageUrl: string;
  footerTagline: string;
  profileImageUrl: string;
  bioBackgroundDesktopUrl: string;
  bioBackgroundMobileUrl: string;
  tallyContactFormId: string;
}

function padToLength<T>(arr: T[], len: number, fill: T = "" as T): T[] {
  const copy = [...arr].slice(0, len);
  while (copy.length < len) copy.push(fill);
  return copy;
}

const DEFAULT_TITLE = `${BRAND_PERSON_NAME} | Modelo · Buenos Aires · Redes y crecimiento`;
const DEFAULT_DESCRIPTION =
  "Modelo de Buenos Aires, 18 años. Apasionada por las redes sociales y el crecimiento. Campañas, colaboraciones y comunidad.";

const EMPTY_SETTINGS: SiteSettings = {
  headerUrl: "/",
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  logoUrl: "",
  galleryImageUrls: Array(9).fill(""),
  bannerImageUrl: "",
  footerTagline: "",
  profileImageUrl: "",
  bioBackgroundDesktopUrl: "",
  bioBackgroundMobileUrl: "",
  tallyContactFormId: "",
};

function parseSettings(s: Record<string, unknown> | null): SiteSettings {
  if (!s) return EMPTY_SETTINGS;
  return {
    headerUrl: (s.headerUrl as string) ?? "/",
    title: (s.title as string) ?? DEFAULT_TITLE,
    description: (s.description as string) ?? DEFAULT_DESCRIPTION,
    logoUrl: (s.logoUrl as string) ?? "",
    galleryImageUrls: padToLength(
      Array.isArray(s.galleryImageUrls) ? (s.galleryImageUrls as string[]) : [],
      9,
    ),
    bannerImageUrl: (s.bannerImageUrl as string) ?? "",
    footerTagline: (s.footerTagline as string) ?? "",
    profileImageUrl: (s.profileImageUrl as string) ?? "",
    bioBackgroundDesktopUrl: (s.bioBackgroundDesktopUrl as string) ?? "",
    bioBackgroundMobileUrl: (s.bioBackgroundMobileUrl as string) ?? "",
    tallyContactFormId: (s.tallyContactFormId as string) ?? "",
  };
}

export default function BaseTemplateAdmin() {
  const { toast } = useToast();
  const { getAuthHeaders } = useAdminAuth();
  const [settings, setSettings] = useState<SiteSettings>(EMPTY_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Snapshot of the last saved state — compared on every render to derive hasChanges.
  const savedRef = useRef<string>("");

  // Derived: true when form differs from the last persisted snapshot.
  // setIsSaving(false) always triggers a re-render so this stays accurate.
  const hasChanges =
    savedRef.current !== "" &&
    JSON.stringify(settings) !== savedRef.current;

  const loadSettings = useCallback(
    async (silent = false) => {
      setLoadError(null);
      if (!silent) setIsLoading(true);
      try {
        const headers = await getAuthHeaders();
        const response = await fetch("/api/admin/site-settings", { headers });
        if (!response.ok) {
          const err = (await response.json()) as { error?: string };
          throw new Error(err.error ?? "Error al cargar");
        }
        const data = (await response.json()) as { live: Record<string, unknown> };
        const parsed = parseSettings(data.live);
        setSettings(parsed);
        savedRef.current = JSON.stringify(parsed);
      } catch (error) {
        const msg =
          error instanceof Error
            ? error.message
            : "No se pudo cargar la configuración";
        setLoadError(msg);
        toast({ title: "Error al cargar", description: msg, variant: "destructive" });
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [getAuthHeaders, toast],
  );

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch("/api/admin/site-settings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ variant: "live", ...settings }),
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "No se pudo guardar");
      }
      savedRef.current = JSON.stringify(settings);
      toast({
        title: "✓ Guardado en vivo",
        description: "Los cambios ya están aplicados al sitio.",
      });
    } catch (error) {
      toast({
        title: "Error al guardar",
        description:
          error instanceof Error ? error.message : "No se pudo guardar",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (savedRef.current) {
      setSettings(JSON.parse(savedRef.current) as SiteSettings);
    }
  };

  // Helper to update a single field.
  const set = useCallback(
    <K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-24">
      {/* ── Page header + action bar ─────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <AdminPageHeader description="Header, banner, logo y textos del shell BaseTemplate (live). Mux/template por país → Home por país." />
        <div className="flex shrink-0 items-center gap-2 pt-1">
          {hasChanges && (
            <span className="text-xs font-medium text-amber-500">
              Sin guardar
            </span>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/" target="_blank">
              <Eye className="mr-2 h-4 w-4" />
              Ver sitio
            </Link>
          </Button>
          <Button onClick={handleSave} disabled={isSaving} size="sm">
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isSaving ? "Guardando..." : "Guardar en vivo"}
          </Button>
        </div>
      </div>

      {/* ── Error banner ─────────────────────────────────────────────── */}
      {loadError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-center justify-between pt-6">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button variant="outline" size="sm" onClick={() => loadSettings()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Identidad visual ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4" />
            Identidad visual
          </CardTitle>
          <CardDescription>Logo, banner hero y galería de imágenes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div className="space-y-2">
            <Label htmlFor="logoUrl">Logo del header</Label>
            <Input
              id="logoUrl"
              value={settings.logoUrl}
              onChange={(e) => set("logoUrl", e.target.value)}
              placeholder={`header-${BRAND_PERSON_NAME.toLowerCase().replace(/\s+/g, "")} o https://res.cloudinary.com/...`}
            />
            <p className="text-xs text-muted-foreground">
              URL completa o public_id de Cloudinary.
            </p>
          </div>

          {/* Banner */}
          <div className="space-y-2">
            <Label>Banner hero (fondo principal de la home)</Label>
            <p className="text-xs text-muted-foreground">
              Si está vacío, se usa la primera imagen de la galería. URL o
              public_id de Cloudinary.
            </p>
            <Input
              value={settings.bannerImageUrl}
              onChange={(e) => set("bannerImageUrl", e.target.value)}
              placeholder="URL o public_id (ej: portfolio/banner)"
            />
            <ImageUploader
              preferCloudinary
              cloudinaryFolder="banner"
              onImageAdded={(url) => set("bannerImageUrl", url)}
            />
          </div>

          {/* Gallery */}
          <div className="space-y-2">
            <Label>Galería de imágenes (hasta 9)</Label>
            <p className="text-xs text-muted-foreground">
              Solo Cloudinary (URL o public_id). Se aplica watermark. Si el
              portfolio tiene imágenes, reemplazan esta galería.
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {Array.from({ length: 9 }, (_, i) => (
                <Input
                  key={i}
                  value={settings.galleryImageUrls[i] ?? ""}
                  onChange={(e) => {
                    const next = [...settings.galleryImageUrls];
                    next[i] = e.target.value;
                    set("galleryImageUrls", next);
                  }}
                  placeholder={`Imagen ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Foto de perfil (bio-link) */}
          <div className="space-y-2">
            <Label>Foto de perfil (bio-link)</Label>
            <p className="text-xs text-muted-foreground">
              Foto redonda del header en el bio-link. Si está vacío, se usa la
              imagen por defecto. URL o public_id de Cloudinary.
            </p>
            <Input
              value={settings.profileImageUrl}
              onChange={(e) => set("profileImageUrl", e.target.value)}
              placeholder="URL o public_id (ej: profile/foto)"
            />
            <ImageUploader
              preferCloudinary
              cloudinaryFolder="profile"
              onImageAdded={(url) => set("profileImageUrl", url)}
            />
          </div>

          {/* Fondo bio-link (desktop) */}
          <div className="space-y-2">
            <Label>Fondo bio-link (desktop)</Label>
            <p className="text-xs text-muted-foreground">
              Imagen de fondo del bio-link en escritorio. Vacío = fondo por
              defecto. URL o public_id de Cloudinary.
            </p>
            <Input
              value={settings.bioBackgroundDesktopUrl}
              onChange={(e) => set("bioBackgroundDesktopUrl", e.target.value)}
              placeholder="URL o public_id (ej: bio-bg-desktop/fondo)"
            />
            <ImageUploader
              preferCloudinary
              cloudinaryFolder="bio-bg-desktop"
              onImageAdded={(url) => set("bioBackgroundDesktopUrl", url)}
            />
          </div>

          {/* Fondo bio-link (mobile) */}
          <div className="space-y-2">
            <Label>Fondo bio-link (mobile)</Label>
            <p className="text-xs text-muted-foreground">
              Imagen de fondo del bio-link en móvil. Vacío = fondo por defecto.
              URL o public_id de Cloudinary.
            </p>
            <Input
              value={settings.bioBackgroundMobileUrl}
              onChange={(e) => set("bioBackgroundMobileUrl", e.target.value)}
              placeholder="URL o public_id (ej: bio-bg-mobile/fondo)"
            />
            <ImageUploader
              preferCloudinary
              cloudinaryFolder="bio-bg-mobile"
              onImageAdded={(url) => set("bioBackgroundMobileUrl", url)}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Textos y SEO ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Textos y SEO</CardTitle>
          <CardDescription>
            Título, descripción, enlace del logo y tagline del footer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="headerUrl">Enlace del logo</Label>
            <Input
              id="headerUrl"
              value={settings.headerUrl}
              onChange={(e) => set("headerUrl", e.target.value)}
              placeholder="/"
            />
            <p className="text-xs text-muted-foreground">
              Destino al clic en el logo:{" "}
              <code className="rounded bg-muted px-1">/</code> para inicio, o
              URL completa (https://…). No uses public_id aquí.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título (hero + meta SEO)</Label>
            <Input
              id="title"
              value={settings.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={DEFAULT_TITLE}
            />
            <p className="text-xs text-muted-foreground">
              Formato:{" "}
              <code className="rounded bg-muted px-1">Nombre | Subtítulo</code>
              {" "}— H1 = solo nombre; subtítulo en{" "}
              <code className="rounded bg-muted px-1">&lt;p&gt;</code>. También
              en meta title.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción (hero + meta SEO)</Label>
            <Textarea
              id="description"
              value={settings.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Aparece en el hero de la página inicial y en meta description.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="footerTagline">Tagline del footer</Label>
            <Textarea
              id="footerTagline"
              value={settings.footerTagline}
              onChange={(e) => set("footerTagline", e.target.value)}
              rows={2}
              placeholder="Frase corta bajo el nombre. Vacío = texto i18n por defecto."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tallyContactFormId">Formulario de contacto (Tally)</Label>
            <Input
              id="tallyContactFormId"
              value={settings.tallyContactFormId}
              onChange={(e) => set("tallyContactFormId", e.target.value)}
              placeholder="ID del form Tally (ej: vGxlv4)"
            />
            <p className="text-xs text-muted-foreground">
              Solo el ID del formulario Tally (el código en{" "}
              <code className="rounded bg-muted px-1">tally.so/embed/&lt;ID&gt;</code>).
              Vacío = formulario por defecto.
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() =>
              setSettings((prev) => ({
                ...prev,
                title: DEFAULT_TITLE,
                description: DEFAULT_DESCRIPTION,
              }))
            }
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            Restaurar título y descripción por defecto
          </Button>
        </CardContent>
      </Card>

      {/* Home internacional → gestión dedicada (borrador + publicar) */}
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4" />
            Home por país (Mux + membresía)
          </CardTitle>
          <CardDescription>
            La plantilla de <code className="rounded bg-muted px-1 text-xs">/</code> y el
            video Mux por país se gestionan en una pantalla dedicada con borrador y
            publicación. No se editan desde acá para evitar pisar la configuración.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/admin/country-templates">
              Abrir Home por país
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* ── Sticky save bar (only when changes exist) ────────────────── */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Tenés cambios sin guardar
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscard}
                disabled={isSaving}
              >
                Descartar
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isSaving ? "Guardando..." : "Guardar en vivo"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
